import { ActionButton, FontSizes, FontWeights, getTheme, IButtonStyles, Icon, IIconProps, IIconStyleProps, IIconStyles, ISearchBoxStyles, IStackItemStyles, IStyleFunctionOrObject, mergeStyles, Persona, PersonaSize, SearchBox, Stack } from "@fluentui/react";
import { Text } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import UserSettingsPanel from "../src/taskpane/components/panels/UserSettingsPanel";
import { RootState } from "../src/taskpane/components/_v2/redux/storeMain";
import { IAuthAccount } from "../src/taskpane/interfaces/IAuthTokenResultDto";
import { toggleIsContentManagerSideMenuCollapsed } from "../src/taskpane/redux/modules/contentManagerDirectory";
import { getUserProfilePicture } from "../src/taskpane/services/GraphService";
import authModule from "../utilities/AuthModule";
import { LanguageContext } from "../utilities/LocalizationModule";
import { logger } from "../utilities/logger";
import { getHeaderTextColor } from "../utilities/ThemeHelper";
import DropDownSearch from "../src/taskpane/components/directoryComponents/DropDownSearch";

const theme = getTheme();

const getHeaderStackStyles = (appColor?: string) => {
  const headerStackStyles: IStackItemStyles = {
    root: {
      minHeight: 50,
      width: "100%",
      borderBottom: "1px solid #edebe9",
      backgroundColor: appColor ? appColor : "#0001FF",
      color: getHeaderTextColor(appColor)
    }
  };

  return headerStackStyles;
}

const getHeaderTextStyles = (appColor?: string) => {
  const headerTextStyles = mergeStyles({
    fontWeight: FontWeights.semibold,
    color: getHeaderTextColor(appColor)
  });

  return headerTextStyles;
}

const headerStackItemStyles: IStackItemStyles = {
  root: {
    paddingLeft: 20,
    paddingRight: 20,
  }
};

const headerTitleStackItemStyles: IStackItemStyles = {
  root: {
    "@media only screen and (max-width: 479px)": { display: "none" },
  }
};

const headerLogoStackItemStyles: IStackItemStyles = {
  root: {
    display: 'flex',
    height: '100%',
    justifyContent: "center",
    "@media only screen and (max-width: 479px)": { display: "none" },
  }
};

const searchBoxStyles: Partial<ISearchBoxStyles> = {
  root: {
    width: "25vw",
    maxWidth: "25vw",
    borderColor: theme.palette.themeDarkAlt,
    position: "relative",
    zIndex: 9998,
    "@media only screen and (max-width: 479px)": { width: "60vw" },
  },
  iconContainer: {
    alignItems: "center"
  }
};

const settingsButtonStyles: IButtonStyles = {
  root: {
    paddingRight: 12,
  },
  icon: {
    fontSize: FontSizes.size20,
  },
};

const getSettingsButtonIconStyles = (appColor?: string) => {
  const settingsButtonIconProps: IIconProps = {
    iconName: "Settings",
    styles: {
      root: {
        color: getHeaderTextColor(appColor)
      }
    }
  }
  return settingsButtonIconProps;
}

const collapseMenuIconStyles: IStyleFunctionOrObject<IIconStyleProps, IIconStyles> = {
  root: {
    fontSize: 18,
    paddingTop: 7,
    paddingRight: 10,
    marginLeft: -5,
    selectors: {
      ":hover": { cursor: 'pointer' }
    }
  }
}
export interface IContentManagerHeaderProps {
  account?: IAuthAccount;
  isInTeamsMode?: boolean;
}

export const ContentManagerHeader = ({ account, isInTeamsMode }: IContentManagerHeaderProps) => {
  const dispatch = useDispatch();

  const tenantInfo = useSelector((state: RootState) => state.tenantInfo);

  const languageStrings = React.useContext(LanguageContext);
  const [isUserSettingsPanelOpen, setIsUserSettingsPanelOpen] = React.useState<boolean>(false);
  const [profileImageUrl, setProfileImageUrl] = React.useState<string>();

  React.useEffect(() => {
    (async () => {
      try {
        const profileImageUrl = await getUserProfilePicture(await authModule.getGraphAccessToken());
        if (profileImageUrl) {
          setProfileImageUrl(profileImageUrl);
        }
      } catch (error) {
        logger(error, "ContentManagerHeader.tsx (getUserProfilePicture)");
      }
    })();
  }, []);

  const ProfileIcon = (profileImageUrl: string) => {
    return (
      <div
        style={{ paddingRight: 16, cursor: "pointer" }}
        onClick={() => { setIsUserSettingsPanelOpen(true) }}>
        <Persona
          imageUrl={profileImageUrl}
          text={account?.displayName}
          hidePersonaDetails
          size={PersonaSize.size32}
        />
      </div>
    )
  };

  const UserSettingsButton = () => {
    return (
      <ActionButton iconProps={getSettingsButtonIconStyles(tenantInfo?.appColor)} onClick={() => setIsUserSettingsPanelOpen(true)} styles={settingsButtonStyles} />
    )
  };

  const getAppTitle = () => {
    if (tenantInfo?.appTitle) {
      return tenantInfo.appTitle;
    }
    return (!isInTeamsMode ? "Docubird " : "") + languageStrings.ContentManager
  }

  const searchBox = (
    <DropDownSearch searchBoxStyles={searchBoxStyles} />
  );

  return (
    <>
      <Stack horizontal verticalAlign="center" horizontalAlign="space-between" styles={getHeaderStackStyles(tenantInfo?.appColor)}>
        <Stack.Item styles={headerStackItemStyles} className="header-container">
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }} style={{ height: "100%" }}>
            <Stack.Item>
              <Icon iconName="CollapseMenu" styles={collapseMenuIconStyles} onClick={() => dispatch(toggleIsContentManagerSideMenuCollapsed())} />
            </Stack.Item>
            {tenantInfo?.appLogo && <Stack.Item align="center" styles={headerLogoStackItemStyles}>
              <img src={tenantInfo.appLogo} className="header-logo" />
            </Stack.Item>}
            <Stack.Item styles={headerTitleStackItemStyles}>
              <Text variant="large" className={getHeaderTextStyles(tenantInfo?.appColor)}>{getAppTitle()}</Text>
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item styles={headerStackItemStyles}>
          {searchBox}
        </Stack.Item>
        {
          account && (!isInTeamsMode ? ProfileIcon(profileImageUrl) : UserSettingsButton())
        }
      </Stack>
      {isUserSettingsPanelOpen && <UserSettingsPanel profileImageUrl={profileImageUrl} isOpen={isUserSettingsPanelOpen} closePanel={() => setIsUserSettingsPanelOpen(false)} />}
    </>
  )
};